import { createContext, useContext, useReducer, useEffect, FC } from 'react';

import { isServer } from '../lib/util';

type Action =
  | { type: 'SET_ALLOW_CHARGES'; payload: boolean }
  | { type: 'SET_LIST_NAME'; payload: string }
  | {
      type: 'VALID_COUPON_ENTERED';
      validCoupon: string;
      productId: string | null;
    };

type Dispatch = (action: Action) => void;

type State = {
  validCoupon: string | null;
  productId: string | null;
  allowCharges: boolean;
  listName: string | null;
};

const initialState = {
  validCoupon: null,
  productId: null,
  allowCharges: false,
  listName: null
};

export const ValueStateContext = createContext<State>(initialState);
export const ValueDispatchContext = createContext<Dispatch>(() => {
  return;
});

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'SET_ALLOW_CHARGES': {
      return {
        ...state,
        allowCharges: action.payload
      };
    }
    case 'VALID_COUPON_ENTERED': {
      return {
        ...state,
        validCoupon: action.validCoupon,
        productId: action.productId
      };
    }
    case 'SET_LIST_NAME': {
      return {
        ...state,
        listName: action.payload
      };
    }
    default:
      return state;
  }
}

export function useValue(): [State, Dispatch] {
  const valueState = useContext(ValueStateContext);
  if (valueState === undefined) {
    throw new Error('useValueState must be used within a ValueStateContext');
  }
  const valueDispatch = useContext(ValueDispatchContext);
  if (valueDispatch === undefined) {
    throw new Error(
      'useValueDispatch must be used within a ValueDispatchContext'
    );
  }
  return [valueState, valueDispatch];
}

export const CheckoutValueContextProvider: FC<{
  storageKey: string | null;
}> = ({ storageKey, children }) => {
  // Try to fetch initialState from sessionStorage if storageKey
  let _initialState = initialState;
  if (storageKey && !isServer && sessionStorage) {
    const initialStateFromStorage = sessionStorage.getItem(storageKey);
    if (initialStateFromStorage) {
      _initialState = JSON.parse(initialStateFromStorage);
    }
  }

  const [state, dispatch] = useReducer(reducer, _initialState);

  useEffect(() => {
    if (!storageKey || !sessionStorage) {
      return;
    }
    sessionStorage.setItem(storageKey, JSON.stringify(state));
  }, [state]);

  return (
    <ValueStateContext.Provider value={state}>
      <ValueDispatchContext.Provider value={dispatch}>
        {children}
      </ValueDispatchContext.Provider>
    </ValueStateContext.Provider>
  );
};
